import { noop } from 'lodash';
import getSymbolFromCurrency from 'currency-symbol-map';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import { InteractionManager, Keyboard, Platform, View, } from 'react-native';
import Animated, { Extrapolation, interpolate, useAnimatedScrollHandler, useAnimatedStyle, useSharedValue, } from 'react-native-reanimated';
import Button, { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import Spacer from 'design-system/Spacer';
import { HelpCallToAction } from 'design-system/HelpCallToAction';
import { AppView } from 'design-system/AppView';
import { NavigationHeader } from 'design-system/NavigationHeader';
import Indicator from 'design-system/Indicator';
import { NavigationTextButton, TextButtonLink } from 'design-system/TextButton';
import Text, { lineHeight, styles } from 'design-system/Text';
import { selectIsScrambled, useFocusEffect, useHeaderHeight } from 'hooks';
import { useActionSheet } from 'hooks/useActionSheet';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { selectDefaultCurrency, selectHasSubcategories, selectNormalizedCategories, } from 'reducers/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import createStyleSheets from 'utils/createStyleSheets';
import { MoneyTextInput } from 'utils/packages/moneyInput';
import { useMoneyInputText } from 'hooks/useMoneyInputText';
import { extractKeyFromKey } from 'utils/flatListHelpers';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useNativeStackScreenOptions } from 'hooks/navigation/useStackNavigatorScreenOptions';
import { useRequiredBenefitCallbackWithSpaces } from 'features/premium/hooks/useRequiredBenefitCallback';
import Section from 'design-system/Section';
import IconPlus from 'design-system/icons/IconPlus';
import { fetchAnalyticsBudgetingData, getIncomes } from 'actions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { CardView } from 'design-system';
import Amount from 'design-system/Amount';
import TouchableBounceCard from 'utils/packages/TouchableBounceCard';
import { useKeyboard } from 'hooks/useKeyboard';
import { KeyboardAwareScrollView, } from 'react-native-keyboard-controller';
import FlatListItemCard from 'design-system/FlatListItemCard';
import { formatAmount } from 'utils/formatting';
import useShouldShowUpgrade from 'features/premium/hooks/useShouldShowUpgrade';
import BlurAmountView from 'design-system/BlurAmountView';
import { getBudgets as getBudgetsAction } from '../actions';
import Amplitude from '../../../utils/amplitude';
import Links from '../../../utils/links';
import { BudgetActionRow } from '../components/BudgetActionRow';
import BudgetCategoryEditRow from '../components/BudgetCategoryEditRow';
import SetCategoryBudgetsAllocation, { allocationCardHeight, } from '../components/SetCategoryBudgetsAllocation';
import { useBudgetRollover } from '../hooks/useBudgetRollover';
import { useClearCategoryBudgetsModal } from '../hooks/useClearCategoryBudgetsModal';
import { useSaveBudget } from '../hooks/useSaveBudget';
import { selectBudgets, selectCategoryBudgets } from '../selectors';
import { isAndroid, isWeb } from '../../../constants';
import BudgetCategoryExcludedRow from '../components/BudgetCategoryExcludedRow';
const openHelp = () => {
    Links.openLink('https://help.emma-app.com/en/category/budgets-1s6usi4/');
};
const EXTRA_KEYBOARD_SPACE = rem(80);
const keyExtractor = isWeb
    ? extractKeyFromKey('key')
    : (_, index) => String(index);
const getPageTitle = (flow) => {
    if (isWeb) {
        if (flow === 'Set') {
            return 'Set Category Budgets';
        }
        if (flow === 'Confirm') {
            return 'Confirm Category Budgets';
        }
        return 'Edit Category Budget';
    }
    return undefined;
};
const sortByTotalLimit = (budgets, limitMap) => [...budgets].sort((a, b) => (limitMap ? limitMap.get(b.key)?.totalLimit ?? 0 : b.totalLimit ?? 0) -
    (limitMap ? limitMap.get(a.key)?.totalLimit ?? 0 : a.totalLimit ?? 0));
const renderScrollComponent = (props) => (<KeyboardAwareScrollView {...props} bottomOffset={EXTRA_KEYBOARD_SPACE} 
// On iOS, layout mode grows a spacer below the list every frame while the
// keyboard moves, a layout pass per frame on the New Architecture. Insets mode
// moves the content inset instead.
mode={isAndroid ? 'layout' : 'insets'}/>);
const SetCategoryBudgetsScreen = ({ navigation, route, }) => {
    const colors = useColors();
    const goBack = route.params.goBack === undefined ? true : route.params.goBack;
    const { categoryId, flow, callback, hasPrefilledBudgets, scrollToParentCategoryId, } = route.params;
    const userCurrency = useAppSelector(selectDefaultCurrency);
    const styles = useStyles(styleSet);
    const [isFocused, setIsFocused] = React.useState(false);
    const budgets = useAppSelector(selectBudgets);
    const categoryBudgets = useAppSelector(selectCategoryBudgets);
    const isFetchingBudgets = useAppSelector((store) => store.budgeting.isFetchingBudgets);
    const hasSubcategories = useAppSelector(selectHasSubcategories);
    const shouldResetRollingAmountRef = useRef(false);
    const scrollViewRef = useRef(null);
    const dispatch = useAppDispatch();
    const { isUnlockedForSpace, onPressUpgrade } = useShouldShowUpgrade({
        benefitId: 'rollingBudgets',
    });
    const scrambled = useAppSelector(selectIsScrambled);
    const settingBudget = route.params.monthlyBudget;
    const monthlyBudget = useMemo(() => {
        const overall = budgets.find((budget) => budget.key === 'overall.monthly');
        return settingBudget !== undefined
            ? {
                totalLimit: settingBudget,
                currency: userCurrency,
                key: 'overall.monthly',
                type: 'overall',
                previousPeriodAverage: overall?.previousPeriodAverage || 0,
            }
            : overall;
    }, [budgets, settingBudget, userCurrency]);
    const [totalBudget, setTotalBudget] = useState(monthlyBudget?.totalLimit ?? null);
    const displayBudget = useMemo(() => totalBudget ?? 0, [totalBudget]);
    useEffect(() => {
        if (monthlyBudget?.totalLimit !== undefined) {
            setTotalBudget(monthlyBudget?.totalLimit ?? null);
        }
    }, [monthlyBudget?.totalLimit]);
    const routeBudgetStateMap = useMemo(() => route.params.budgetState
        ? new Map(route.params.budgetState?.map((rb) => [rb.key, rb]) ?? [])
        : undefined, [route.params.budgetState]);
    const [budgetState, setBudgetState] = useState(() => {
        const updatedCategories = categoryBudgets.map((budget) => 
        // Populate the category budgets with the limit from the previous screen if it was there
        routeBudgetStateMap?.get(budget.key) || budget);
        return sortByTotalLimit(updatedCategories, routeBudgetStateMap);
    });
    const baseLimit = useMemo(() => budgets.find((budget) => budget.key === 'overall.monthly')?.baseLimit ||
        0, [budgets]);
    const overAllMonthlyBudget = useMemo(() => budgets.find((budget) => budget.key === 'overall.monthly'), [budgets]);
    const overAllRolledAmount = overAllMonthlyBudget?.rollingAccumulatedLimit || 0;
    const onResetRollingAmounts = useCallback((resetAll) => {
        if (resetAll) {
            shouldResetRollingAmountRef.current = true;
            setTotalBudget(baseLimit);
        }
        setBudgetState((state) => state.map((budget) => ({
            ...budget,
            rollingAccumulatedLimit: null,
            totalLimit: budget.baseLimit,
        })));
    }, [baseLimit]);
    const clearBudgets = useCallback(() => {
        setBudgetState((state) => state.map((budget) => ({
            ...budget,
            totalLimit: 0,
            rollingAccumulatedLimit: null,
        })));
    }, []);
    const navRightBtnRef = useRef(null);
    const openCategoryBudgetsModal = useClearCategoryBudgetsModal({
        onClearRollingPress: onResetRollingAmounts,
        onClearBudgetPress: clearBudgets,
        isRolling: budgets.some((budget) => !!budget.rollingAccumulatedLimit),
        btnViewRef: navRightBtnRef,
    });
    const handleClear = useCallback(() => {
        openCategoryBudgetsModal();
    }, [openCategoryBudgetsModal]);
    const scrollY = useSharedValue(0);
    // eslint-disable-next-line no-nested-ternary
    const rolledHeight = overAllRolledAmount
        ? isUnlockedForSpace
            ? rem(21)
            : rem(29)
        : 0;
    const [headerInfoHeight, setHeaderInfoHeight] = useState(rem(109));
    const globalScreenOptions = useNativeStackScreenOptions();
    const showTotalBudget = !(flow === 'Set' && !hasPrefilledBudgets);
    const totalHeaderHeight = showTotalBudget
        ? headerInfoHeight + rolledHeight
        : headerInfoHeight;
    const titleContainerStyle = useAnimatedStyle(() => ({
        opacity: interpolate(scrollY?.value, [0, totalHeaderHeight], [0, 1], Extrapolation.CLAMP),
    }));
    const modalShownRef = useRef(false);
    useFocusEffect(useCallback(() => {
        modalShownRef.current = route.params.supressEditModal || false;
    }, [route.params.supressEditModal]));
    const handleModalShown = () => {
        if (!modalShownRef.current) {
            modalShownRef.current = true;
        }
    };
    useLayoutEffect(() => {
        navigation.setOptions({
            title: getPageTitle(route.params.flow),
            headerTitle: '',
            headerRight: isWeb
                ? () => (<View ref={navRightBtnRef}>
              <NavigationTextButton text="Clear" onPress={handleClear} style={styles.clearButtonWeb}/>
            </View>)
                : () => <NavigationTextButton text="Clear" onPress={handleClear}/>,
            headerTintColor: colors.text.primary,
        });
    }, [
        colors.text.primary,
        flow,
        globalScreenOptions.headerTitleStyle,
        handleClear,
        hasPrefilledBudgets,
        route.params.flow,
        styles.clearButtonWeb,
        styles.headerTitleContainer,
        titleContainerStyle,
    ]);
    useEffect(() => {
        // User probably added a new category, or we just loaded in budgets
        setBudgetState((s) => {
            const updatedCategories = categoryBudgets.map((budget) => {
                const curr = s.find((b) => b.key === budget.key);
                return curr || budget;
            });
            return sortByTotalLimit(updatedCategories, routeBudgetStateMap);
        });
    }, [categoryBudgets, routeBudgetStateMap]);
    const categories = useAppSelector(selectNormalizedCategories);
    const data = useMemo(() => {
        const budgetsMap = budgetState.reduce((acc, budget) => {
            const category = categories[budget.categoryId || ''];
            if (category) {
                acc[category.id] = {
                    ...budget,
                    parentCategoryId: category.parentCategoryId,
                };
            }
            return acc;
        }, {});
        const visibleBudgets = [];
        const hiddenBudgets = [];
        Object.values(budgetsMap).forEach((budget) => {
            const item = {
                ...budget,
                subcategories: categories[budget.categoryId || '']?.subCategoryIds
                    ?.map((id) => budgetsMap[id])
                    .filter((budget) => budget !== undefined),
            };
            if (item.parentCategoryId)
                return;
            if (categories[budget.categoryId || '']?.canSetBudget) {
                visibleBudgets.push(item);
            }
            else {
                hiddenBudgets.push({ ...item, isExcluded: true });
            }
        });
        if (hiddenBudgets.length > 0) {
            return visibleBudgets
                .map((item, index) => ({
                ...item,
                isFirst: hasSubcategories ? index === 0 : false,
                isLast: index === visibleBudgets.length - 1,
            }))
                .concat({
                key: 'excluded_categories',
                sectionHeader: 'Excluded categories',
                isFirst: true,
                isLast: true,
            }, hiddenBudgets.map((item, index) => ({
                ...item,
                isFirst: index === 0,
                isLast: index === hiddenBudgets.length - 1,
            })));
        }
        return visibleBudgets.map((item, index) => ({
            ...item,
            isFirst: hasSubcategories ? index === 0 : false,
            isLast: index === visibleBudgets.length - 1,
        }));
    }, [budgetState, categories, hasSubcategories]);
    const headerHeight = useHeaderHeight();
    useEffect(() => {
        const idToScrollTo = scrollToParentCategoryId || categoryId;
        if (!idToScrollTo)
            return () => { };
        let timeout;
        InteractionManager.runAfterInteractions(() => {
            timeout = setTimeout(() => {
                let parentIndex = data.findIndex((budget) => 'categoryId' in budget && budget.categoryId === idToScrollTo);
                const parentCategoryId = categories[idToScrollTo]?.parentCategoryId;
                let subcategoryIndex = -1;
                if (parentIndex === -1 && parentCategoryId) {
                    parentIndex = data.findIndex((budget) => 'categoryId' in budget && budget.categoryId === parentCategoryId);
                    const parent = data[parentIndex];
                    subcategoryIndex =
                        parent && 'subcategories' in parent
                            ? parent.subcategories?.findIndex((budget) => idToScrollTo === budget.categoryId) ?? -1
                            : -1;
                }
                if (parentIndex < 0)
                    return;
                const extraOffset = subcategoryIndex !== -1
                    ? (subcategoryIndex + 1) * rem(81) + rem(56)
                    : 0;
                // @ts-ignore
                scrollViewRef.current?.scrollToIndex?.({
                    index: parentIndex,
                    animated: true,
                    viewOffset: headerHeight + rem(136) - extraOffset,
                });
            }, 400);
        });
        return () => {
            clearTimeout(timeout);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const totalSpending = useMemo(() => {
        const sum = data.reduce((prev, budget) => {
            if ('sectionHeader' in budget)
                return prev;
            if (budget.isExcluded)
                return prev;
            if (budget.type !== 'overall' && budget.totalLimit) {
                return prev + budget.totalLimit;
            }
            return prev;
        }, 0);
        return Math.round((sum + Number.EPSILON) * 100) / 100;
    }, [data]);
    const onPressManageBudgets = useCallback(() => {
        Keyboard.dismiss();
        navigation.navigateDeprecated('ManageCategories');
        Amplitude.logEvent('Budgets.CustomCategorySetup');
    }, []);
    const onManageExcluded = useCallback(() => {
        navigation.navigateDeprecated('ExcludeCategories', {
            onSaveLocal: setBudgetState,
            skipSaveBudget: flow === 'Confirm',
            budgets: budgetState,
            ...(flow === 'Confirm' && {
                title: 'Want to exclude categories from your budget?',
                subtitle: 'Choose categories to exclude from Budgeting and Analytics. Transactions in these categories won’t be included in your spending totals.',
            }),
        });
    }, [budgetState, flow]);
    const onPressAddNewCategory = useCallback(() => {
        navigation.navigateDeprecated('CreateCustomCategory');
    }, []);
    const actionSheet = useActionSheet();
    const [saving, save, getBudgets] = useSaveBudget();
    useEffect(() => {
        if (budgets.length === 0) {
            getBudgets();
        }
        Amplitude.logEvent('Category Budgets Screen', { type: 'in_app' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const { isRollover } = useBudgetRollover(route.params.shouldRollover);
    const saveDetails = useRequiredBenefitCallbackWithSpaces(useCallback(async (increaseToMatchTotal) => {
        const editedMonthlyBudget = 
        // eslint-disable-next-line no-nested-ternary
        increaseToMatchTotal
            ? totalSpending
            : displayBudget !== monthlyBudget?.totalLimit
                ? displayBudget
                : undefined;
        const budgetStateAltered = monthlyBudget
            ? [monthlyBudget, ...budgetState]
            : budgetState;
        const newMonthlyBudget = editedMonthlyBudget !== undefined
            ? budgetStateAltered.map((budget) => {
                if (budget.key === 'overall.monthly') {
                    return {
                        ...budget,
                        totalLimit: Number(editedMonthlyBudget) || 0,
                    };
                }
                return budget;
            })
            : budgetStateAltered;
        if (flow === 'Confirm') {
            navigation.navigateDeprecated('EditBudget', {
                flow,
                editedMonthlyBudget,
                shouldRollover: isRollover,
                budgetState: newMonthlyBudget,
                shouldResetRollingAmount: shouldResetRollingAmountRef.current,
                period: route.params.period,
            });
        }
        else if (flow === 'Edit') {
            const res = await save(editedMonthlyBudget, isRollover, newMonthlyBudget, route.params.period, shouldResetRollingAmountRef.current);
            if (res === null)
                return;
            dispatch(getBudgetsAction());
            dispatch(getIncomes());
            dispatch(fetchAnalyticsBudgetingData());
            navigation.navigateDeprecated('EditBudget', {
                flow,
                editedMonthlyBudget,
                shouldRollover: isRollover,
                budgetState: newMonthlyBudget,
                shouldResetRollingAmount: shouldResetRollingAmountRef.current,
                period: route.params.period,
            });
        }
        else if (flow === 'Set') {
            navigation.navigateDeprecated('Rollover', {
                editedMonthlyBudget,
                budgetState: newMonthlyBudget,
                shouldResetRollingAmount: shouldResetRollingAmountRef.current,
                period: route.params.period,
                hasPrefilledBudgets,
            });
        }
        else if (goBack) {
            const res = await save(editedMonthlyBudget, isRollover, newMonthlyBudget, route.params.period, shouldResetRollingAmountRef.current);
            if (res === null)
                return;
            dispatch(getBudgetsAction());
            dispatch(getIncomes());
            dispatch(fetchAnalyticsBudgetingData());
            navigation.goBack();
        }
    }, [
        budgetState,
        displayBudget,
        flow,
        goBack,
        hasPrefilledBudgets,
        isRollover,
        monthlyBudget,
        route.params.period,
        save,
        totalSpending,
    ]), 'budgeting', flow === 'Set' || flow === 'Confirm');
    const onPressSave = useCallback(() => {
        if (monthlyBudget?.totalLimit != null &&
            totalSpending >
                (monthlyBudget.totalLimit !== displayBudget
                    ? displayBudget
                    : monthlyBudget.totalLimit)) {
            const effectiveLimit = monthlyBudget.totalLimit !== displayBudget
                ? displayBudget
                : monthlyBudget.totalLimit;
            const difference = totalSpending - effectiveLimit;
            const currency = budgets && budgets[0] ? budgets[0].currency : '';
            const options = ['Save anyway', 'Increase Total Budget', 'Cancel'];
            const title = `The total of your budgeting categories exceeds your total budget by ${getSymbolFromCurrency(currency)}${difference.toFixed(2)}`;
            actionSheet.showActionSheetWithOptions({
                title,
                options,
                cancelButtonIndex: 2,
            }, (idx) => {
                if (idx !== undefined && idx < 2) {
                    saveDetails(idx === 1);
                }
            });
            return;
        }
        saveDetails();
    }, [
        actionSheet,
        budgets,
        displayBudget,
        monthlyBudget?.totalLimit,
        saveDetails,
        totalSpending,
    ]);
    const onSetBudget = useCallback((key, value) => {
        setBudgetState((state) => state.map((budget) => {
            if (budget.key === key) {
                return {
                    ...budget,
                    totalLimit: value,
                    // reset rolling amount on category budget change
                    rollingAccumulatedLimit: null,
                };
            }
            return budget;
        }));
    }, []);
    const { width, paddingHorizontal } = useAppFrameDimensions(isWeb);
    const hasAverages = useMemo(() => budgetState.find((budget) => typeof budget.previousPeriodAverage === 'number') !== undefined, [budgetState]);
    const { keyboardHeight } = useKeyboard();
    const { paddingBottom } = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING + (isAndroid ? keyboardHeight : 0));
    const contentContainerStyles = useMemo(() => ({
        paddingTop: undefined,
        paddingBottom,
        ...(isWeb
            ? { width, marginLeft: paddingHorizontal }
            : { paddingHorizontal }),
    }), [paddingBottom, paddingHorizontal, width]);
    const linesRef = useRef({});
    const cardHeightRef = useRef({});
    const headerInputRange = useMemo(() => [0, totalHeaderHeight], [totalHeaderHeight]);
    const updatedMeasurement = useCallback(() => {
        const measurements = linesRef.current;
        const cardMeasurements = cardHeightRef.current;
        if (measurements.title !== undefined &&
            measurements.subTitle !== undefined) {
            setHeaderInfoHeight(rem(24 + 4) +
                measurements.title * lineHeight.Text_24 +
                measurements.subTitle * lineHeight.TextThin_16);
        }
        if (cardMeasurements.height !== undefined) {
            setHeaderInfoHeight(rem(24 + 4 + 24 + 24) + cardMeasurements.height);
        }
    }, []);
    const onTextLayoutTitle = useCallback((event) => {
        const lines = event.nativeEvent.lines.length;
        if (linesRef.current.title === lines)
            return;
        linesRef.current = {
            ...linesRef.current,
            title: lines,
        };
        updatedMeasurement();
    }, [updatedMeasurement]);
    const onTextLayoutSubTitle = useCallback((event) => {
        const lines = event.nativeEvent.lines.length;
        if (linesRef.current.subTitle === lines)
            return;
        linesRef.current = {
            ...linesRef.current,
            subTitle: lines,
        };
        updatedMeasurement();
    }, [updatedMeasurement]);
    const onNewFunnelTextLayout = useCallback((event) => {
        if (isWeb) {
            setHeaderInfoHeight(rem(event.nativeEvent.layout.height + 24));
        }
    }, []);
    const onChangeValue = useCallback((newValue) => {
        setTotalBudget(newValue);
    }, []);
    const [totalBudgetText, onChangeTotalBudgetText] = useMoneyInputText(totalBudget, onChangeValue);
    const handleEvent = useCallback((event) => {
        if (isWeb) {
            setIsFocused(event === 'focus');
        }
    }, []);
    const handleBlur = useCallback(() => {
        handleEvent('blur');
    }, [handleEvent]);
    const handleFocus = useCallback(() => {
        handleEvent('focus');
    }, [handleEvent]);
    const TotalBudgetContainer = useMemo(() => (<CardView withPadding>
        <View style={styles.totalBudgetContainer}>
          <View>
            <Text Text-16>Total budget</Text>
            {overAllMonthlyBudget?.rollingAccumulatedLimit ? (<View>
                <BlurAmountView enabled={scrambled}>
                  <Text TextThin-14 Secondary>
                    Default{' '}
                    <Text NumbersThin-14 Secondary>
                      {formatAmount(overAllMonthlyBudget?.baseLimit, overAllMonthlyBudget?.currency, 2)}
                    </Text>
                  </Text>
                </BlurAmountView>
                <BlurAmountView enabled={scrambled}>
                  {!isUnlockedForSpace ? <Spacer h={4}/> : null}
                  <Text TextThin-14 color={overAllMonthlyBudget?.rollingAccumulatedLimit >= 0
                ? colors.status.positive
                : colors.status.negative} {...(!isUnlockedForSpace ? { Gray: true } : {})}>
                    Rolled{' '}
                    {!isUnlockedForSpace ? (<Button title="Upgrade" brandReversed square cards extra small hug onPress={onPressUpgrade}/>) : (<Amount Numbers-14 showSign color={overAllMonthlyBudget?.rollingAccumulatedLimit >= 0
                    ? colors.status.positive
                    : colors.status.negative} value={{
                    value: overAllMonthlyBudget?.rollingAccumulatedLimit,
                    currency: overAllMonthlyBudget?.currency,
                }}/>)}
                  </Text>
                </BlurAmountView>
              </View>) : (<BlurAmountView enabled={scrambled}>
                <Text TextThin-14 Secondary>
                  Avg{' '}
                  <Text NumbersThin-14 Secondary>
                    {formatAmount(overAllMonthlyBudget?.previousPeriodAverage || 0, overAllMonthlyBudget?.currency, 2)}
                  </Text>
                </Text>
              </BlurAmountView>)}
          </View>
          <View style={styles.textInputContainer}>
            <MoneyTextInput value={totalBudgetText} onBlur={handleBlur} returnKeyType="done" onChangeText={onChangeTotalBudgetText} prefix={getSymbolFromCurrency(userCurrency) ?? ''} maximumIntegerDigits={8} onFocus={handleFocus} underlineColorAndroid="transparent" placeholder={`${getSymbolFromCurrency(userCurrency)}0`} style={[styles.textInput, isFocused && styles.focusedStyles]}/>
          </View>
        </View>
      </CardView>), [
        colors.status.negative,
        colors.status.positive,
        handleBlur,
        handleFocus,
        isFocused,
        isUnlockedForSpace,
        onChangeTotalBudgetText,
        onPressUpgrade,
        overAllMonthlyBudget?.baseLimit,
        overAllMonthlyBudget?.currency,
        overAllMonthlyBudget?.previousPeriodAverage,
        overAllMonthlyBudget?.rollingAccumulatedLimit,
        scrambled,
        styles.focusedStyles,
        styles.textInput,
        styles.textInputContainer,
        styles.totalBudgetContainer,
        totalBudgetText,
        userCurrency,
    ]);
    const ListHeaderComponent = useMemo(() => (<>
        {!isWeb ? (<>
            {showTotalBudget ? (TotalBudgetContainer) : (<View style={styles.headerContainer} onLayout={onNewFunnelTextLayout}>
                <Text Text-24 onTextLayout={onTextLayoutTitle}>
                  Select how much you want to spend on each category
                </Text>
                <Spacer h={4}/>
                <Text TextThin-16 Secondary onTextLayout={onTextLayoutSubTitle}>
                  Category budgets will help you track your day to day spending
                </Text>
              </View>)}
            <View style={[
                {
                    height: allocationCardHeight +
                        rem(8) +
                        (displayBudget > 0 ? 0 : rem(-18)),
                },
            ]}/>
          </>) : null}

        <View>
          <Section style={styles.manageSection} title="Categories" button={<TextButtonLink text="Manage" onPress={onPressManageBudgets}/>}/>
          {hasSubcategories ? (<TouchableBounceCard onDark noMarginTop onPress={onPressAddNewCategory}>
              <BudgetActionRow title="Add new category" icon={<IconPlus color={colors.elements.brand}/>}/>
            </TouchableBounceCard>) : (<FlatListItemCard index={0} style={styles.listItem} onPress={onPressAddNewCategory}>
              <BudgetActionRow title="Add new category" icon={<IconPlus color={colors.elements.brand}/>}/>
            </FlatListItemCard>)}
        </View>
      </>), [
        TotalBudgetContainer,
        colors.elements.brand,
        displayBudget,
        hasSubcategories,
        onNewFunnelTextLayout,
        onPressAddNewCategory,
        onPressManageBudgets,
        onTextLayoutSubTitle,
        onTextLayoutTitle,
        showTotalBudget,
        styles.headerContainer,
        styles.listItem,
        styles.manageSection,
    ]);
    const headerComponentWeb = useMemo(() => (<View style={{ width, marginLeft: paddingHorizontal }}>
        {showTotalBudget ? (TotalBudgetContainer) : (<View style={styles.headerContainer} onLayout={onNewFunnelTextLayout}>
            <Text Text-24 onTextLayout={onTextLayoutTitle}>
              Select how much you want to spend on each category
            </Text>
            <Spacer h={4}/>
            <Text TextThin-16 Secondary onTextLayout={onTextLayoutSubTitle}>
              Category budgets will help you track your day to day spending
            </Text>
          </View>)}
      </View>), [
        TotalBudgetContainer,
        onNewFunnelTextLayout,
        onTextLayoutSubTitle,
        onTextLayoutTitle,
        paddingHorizontal,
        showTotalBudget,
        styles.headerContainer,
        width,
    ]);
    const ListFooterComponent = useCallback(() => (<HelpCallToAction style={styles.help} title="Need to amend your budgets?" subTitle="Learn how to do it" onPress={openHelp}/>), [styles.help]);
    const onScrollList = useAnimatedScrollHandler((event) => {
        scrollY.value = event.contentOffset.y;
    });
    const buttonTitle = useMemo(() => {
        if (flow === 'Set') {
            return 'Continue';
        }
        if (callback === undefined || flow === 'Confirm') {
            return 'Save';
        }
        return 'Done';
    }, [callback, flow]);
    const renderItem = useCallback(({ item: budget }) => {
        if ('sectionHeader' in budget) {
            return (<Section style={[styles.manageSection, styles.manageSectionExcluded]} title="Excluded categories" button={<TextButtonLink text="Manage" onPress={onManageExcluded}/>}/>);
        }
        return (<View style={[
                styles.eachItem,
                (hasSubcategories || budget.isFirst) && styles.startOfCard,
                (hasSubcategories || budget.isLast) && [
                    styles.endOfCard,
                    styles.marginBottom,
                ],
            ]}>
          {budget.isExcluded ? (<BudgetCategoryExcludedRow budget={budget} subcategories={budget.subcategories}/>) : (<BudgetCategoryEditRow budget={budget} subcategories={budget.subcategories} modalShownRef={modalShownRef} onModalShown={handleModalShown} key={budget.key} onChange={onSetBudget} hasAverages={hasAverages} initialFocusCategoryId={categoryId} scrollRef={scrollViewRef}/>)}
        </View>);
    }, [
        categoryId,
        hasAverages,
        hasSubcategories,
        onManageExcluded,
        onSetBudget,
        styles.eachItem,
        styles.endOfCard,
        styles.manageSection,
        styles.manageSectionExcluded,
        styles.marginBottom,
        styles.startOfCard,
    ]);
    const baseProps = {
        data,
        ref: scrollViewRef,
        scrollEventThrottle: 16,
        renderItem,
        onScroll: onScrollList,
        keyExtractor,
        onScrollToIndexFailed: noop,
        keyboardShouldPersistTaps: 'always',
        ListFooterComponent,
        ListHeaderComponent,
        contentContainerStyle: contentContainerStyles,
        scrollIndicatorInsets: SCROLL_INDICATOR_INSETS,
    };
    return (<AppView withFrame={false}>
      <NavigationHeader hasShadow={false} scrollY={scrollY} bgColorInputRange={headerInputRange} shadowInputRange={headerInputRange} withFrame title={flow === 'Confirm' || !flow ? 'Category budgets' : 'Your budgets'} hideTitleOnScroll={!showTotalBudget}/>
      <View style={styles.flex}>
        {isFetchingBudgets && !budgets.length ? (<Indicator dark/>) : (<>
            {isWeb ? (<Animated.ScrollView onScroll={onScrollList} ref={scrollViewRef} scrollEventThrottle={16}>
                {headerComponentWeb}
                <View 
            // @ts-ignore
            style={stickyWebStyle}>
                  <SetCategoryBudgetsAllocation scrollY={scrollY} totalAllocated={totalSpending || 0} totalBudget={displayBudget} baseLimit={baseLimit} rolledAmount={overAllRolledAmount} currency={monthlyBudget?.currency || userCurrency} headerInfoHeight={totalHeaderHeight} showZeroBudget={!hasPrefilledBudgets}/>
                </View>
                <Animated.FlatList {...baseProps} scrollEnabled={false}/>
              </Animated.ScrollView>) : (<>
                <Animated.FlatList {...baseProps} renderScrollComponent={renderScrollComponent}/>

                {!!totalHeaderHeight && (<SetCategoryBudgetsAllocation scrollY={scrollY} totalAllocated={totalSpending || 0} totalBudget={displayBudget} baseLimit={baseLimit} rolledAmount={overAllRolledAmount} currency={monthlyBudget?.currency || userCurrency} headerInfoHeight={totalHeaderHeight} showZeroBudget={!hasPrefilledBudgets}/>)}
              </>)}
            {(!isAndroid || !keyboardHeight) && (<Button brand floating onPress={onPressSave} isFetching={saving} title={buttonTitle}/>)}
          </>)}
      </View>
    </AppView>);
};
const stickyWebStyle = {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    marginBottom: rem(8),
};
const styleSet = createStyleSheets((colors) => ({
    listCard: {
        marginTop: 0,
    },
    flex: {
        flex: 1,
    },
    help: {
        backgroundColor: colors.cards.onDark,
    },
    endOfCard: {
        marginHorizontal: rem(16),
        borderBottomLeftRadius: rem(16),
        borderBottomRightRadius: rem(16),
        backgroundColor: colors.cards.onDark,
        ...(Platform.OS === 'ios' && {
            overflow: 'hidden',
        }),
        ...(isWeb && {
            paddingBottom: rem(8),
            paddingHorizontal: rem(8),
        }),
    },
    startOfCard: {
        marginHorizontal: rem(16),
        borderTopLeftRadius: rem(16),
        borderTopRightRadius: rem(16),
        backgroundColor: colors.cards.onDark,
        ...(Platform.OS === 'ios' && {
            overflow: 'hidden',
        }),
        ...(isWeb && {
            paddingTop: rem(8),
            paddingHorizontal: rem(8),
        }),
    },
    eachItem: {
        marginHorizontal: rem(16),
        backgroundColor: colors.cards.onDark,
        ...(isWeb && {
            paddingHorizontal: rem(8),
        }),
    },
    marginBottom: {
        marginBottom: rem(16),
    },
    headerContainer: {
        paddingHorizontal: rem(16),
        paddingBottom: rem(24),
    },
    headerTitleContainer: {
        justifyContent: 'center',
        alignItems: 'center',
    },
    manageSection: { marginTop: rem(16), marginBottom: 0 },
    manageSectionExcluded: { marginTop: rem(8) },
    clearButtonWeb: {
        marginRight: rem(16),
    },
    textWithIconContainer: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    infoIconContainer: {
        marginLeft: rem(10),
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    totalBudgetContainer: {
        flexDirection: 'row',
    },
    editTotalSection: {
        marginTop: rem(16),
        marginBottom: rem(4),
    },
    listItem: {
        backgroundColor: colors.cards.onDark,
    },
    textInputContainer: {
        flex: 1,
        justifyContent: 'center',
        ...(isWeb && {
            alignItems: 'flex-end',
        }),
    },
    textInput: {
        ...styles.Numbers_20,
        flex: 1,
        marginTop: 0,
        paddingTop: 0,
        height: rem(36),
        marginBottom: 0,
        paddingBottom: 0,
        textAlign: 'right',
        paddingLeft: rem(16),
        color: colors.text.primary,
        ...(isWeb && {
            width: rem(150),
            height: rem(49),
            cursor: 'pointer',
            borderWidth: rem(1),
            borderRadius: rem(8),
            paddingHorizontal: rem(16),
            caretColor: colors.text.selection,
            borderColor: colors.borders.divider,
            backgroundColor: colors.background.dark,
        }),
    },
    focusedStyles: {
        ...(isWeb && {
            borderColor: colors.elements.brand,
        }),
    },
}));
export default SetCategoryBudgetsScreen;
